<script setup>
import router from '@/router';
import {onMounted, ref} from 'vue';
import ClientReportService from '@/services/ClientReportService';
import {useRoute} from 'vue-router';
import LineChart from '@/components/echarts/LineChart.vue';
import PieChart from '@/components/echarts/PieChart.vue';
import {formatDate, formatAmount, formatNumber, formatPercents} from '../helper';
import {useI18n} from 'vue-i18n';
import InstructionsTableComponent from '@/components/instructions/InstructionsTableComponent.vue';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import DateRangeModal from '@/components/modals/DateRangeModal.vue';

const {t} = useI18n();
const route = useRoute();
const contractStrategy = ref(null);
const chartData = ref(null);
const chartCategories = ref(null);
const transactions = ref(null);
const trades = ref(null);
const strategies = ref(null);
const loading = ref({
  contract_strategy: true,
  contract_strategy_stats: true,
  contract_strategy_history: true,
  contract_strategy_composition: true,
  contract_strategy_transaction: true,
  contract_strategy_trades: true,
  contract_strategy_instructions: true,
});
const rangeStats = ref(null);
const pendingDeposits = ref([]);
const composition = ref(null);
const instructions = ref([]);
const showInstructions = ref(false);
const dateRange = ref({from: null, to: null});

function navigateTo(to) {
  router.push(to);
}

function getCurrentYearYtdRange() {
  const today = new Date();
  today.setDate(today.getDate() - 1); // včerejšek
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return {
    from: `${year}-01-01`,
    to: `${year}-${month}-${day}`,
  };
}

function openDateRangeModal() {
  const {reveal, onConfirm} = createConfirmDialog(DateRangeModal, {
    fromDate: dateRange.value.from,
    toDate: dateRange.value.to,
    // datum první investice smlouvy/strategie - modal nedovolí vybrat dřívější začátek období
    minDate: rangeStats.value?.first_position_date ?? null,
  });
  reveal();
  onConfirm(async (data) => {
    dateRange.value = data;
    const rangeToUse = (data.from || data.to) ? data : getCurrentYearYtdRange();
    const rangeData = await ClientReportService.getContractStrategyStats(route.params.id, rangeToUse);
    rangeStats.value = rangeData.data;
  });
}

onMounted(async () => {
  // YTD range se načítá přímo v Promise.all, aby nedošlo k race condition:
  // dřívější verze spouštěla paralelně all-time request (bez from/to) i YTD request,
  // přičemž all-time response přepisovala rangeStats na špatný počáteční stav (datum podpisu smlouvy).
  // Opraveno také stínování ref 'stats' lokální proměnnou stejného jména v callbacku.
  Promise.all([
    ClientReportService.getContractStrategy(route.params.id),
    ClientReportService.getContractStrategyStats(route.params.id, getCurrentYearYtdRange()),
  ]).then(([contractStrategyData, ytdStats]) => {
    contractStrategy.value = contractStrategyData.data;
    rangeStats.value = ytdStats.data;
    pendingDeposits.value = ytdStats.data.pending_deposits;
    showInstructions.value = contractStrategy.value.meta?.has_instructions || false;
    if (showInstructions.value) {
      getContractStrategyInstructions();
    }
    loading.value = {
      ...loading.value,
      contract_strategy: false,
      contract_strategy_stats: false,
    };
  });

  ClientReportService.getContractStrategyHistory(route.params.id)
      .then(portfolioHistory => {
        chartData.value = portfolioHistory.data.map(candle => candle.amount);
        chartCategories.value = portfolioHistory.data.map(candle => candle.date);
        loading.value = {
          ...loading.value,
          contract_strategy_history: false,
        };
      });

  Promise.all([
    ClientReportService.getContractStrategyComposition(route.params.id),
    ClientReportService.getContractStrategyTransactions(route.params.id),
    ClientReportService.getContractStrategyTrades(route.params.id)
  ])
      .then(([cmpst, trans, trds]) => {
        transactions.value = trans.data;
        trades.value = trds.data;
        composition.value = cmpst.data;
        strategies.value = [];
        loading.value = {
          ...loading.value,
          contract_strategy_composition: false,
          contract_strategy_transaction: false,
          contract_strategy_trades: false
        };
      });
});

function getContractStrategyInstructions() {
  if (showInstructions.value) {
    ClientReportService.getContractStrategyInstructions(route.params.id).then((response) => {
      instructions.value = response.data;
      loading.value = {
        ...loading.value,
        contract_strategy_instructions: false,
      }
    });
  }
}
</script>

<template>

  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-2">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('general.back') }}</a>
        </div>
        <div class="col-8 text-center">
          <div v-if="loading.contract_strategy" class="d-block">
            <Skeletor width="150" height="22" class="d-inline-block" pill /><br/>
            <Skeletor width="150" height="10" class="mt-2 d-inline-block" pill />
          </div>
          <div v-else>
            <div class="page-section__title">
              {{ contractStrategy?.label }}
            </div>
            <div class="page-section__subtitle">
              {{ contractStrategy?.strategy?.strategy_name }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page-section__content">
      <div class="row">
        <div class="col-md-6 mt-4 col-12">
          <div v-if="loading.contract_strategy_stats" class="text-center">
            <div class="row mb-3">
              <div class="col-12 text-center">
                <div class="row d-none d-md-flex">
                  <div class="col-md-6 text-center" v-for="i in 2" :key="i">
                    <Skeletor width="100%" height="32" pill />
                    <Skeletor width="100%" height="22" pill class="mt-2" />
                  </div>
                </div>
                <div class="row d-md-none">
                  <div class="col-12">
                    <Skeletor width="100%" height="32" pill />
                    <Skeletor width="100%" height="22" pill class="mt-2" />
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12">
                 <Skeletor width="100%" height="150" />
              </div>
            </div>
          </div>
          <div v-if="!loading.contract_strategy_stats && rangeStats">
            <div class="row g-2 g-md-3 mb-3">
              <!-- Hodnota strategie -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">{{ t('contractOverview.value') }}</span>
                    <div class="card__value">
                      {{ formatAmount(contractStrategy.performance.totalAmount, contractStrategy.performance.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Investováno — celková suma vložených prostředků -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">{{ t('dashboardClient.invested') }}</span>
                    <div class="card__value">
                      {{ formatAmount(contractStrategy.performance.investedAmount, contractStrategy.performance.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Výnos za celé období -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">{{ t('dashboardClient.all_period') }}</span>
                    <div class="card__value" :class="{'ci ci--up': contractStrategy.performance.performancePerc > 0, 'ci ci--down': contractStrategy.performance.performancePerc < 0}">
                      <span>{{ contractStrategy.performance.gain > 0 ? '+' : '' }}</span>{{ formatAmount(contractStrategy.performance.gain, contractStrategy.performance.currency) }}
                    </div>
                    <div class="card__subvalue" :class="{'ci ci--up': contractStrategy.performance.performancePerc > 0, 'ci ci--down': contractStrategy.performance.performancePerc < 0}">
                      <span>{{ contractStrategy.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(contractStrategy.performance.performancePerc) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Date range karta — výběr vlastního období, výchozí YTD -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100 cursor-pointer" @click="openDateRangeModal">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">
                      <!-- období z API (rangeStats.range) má přednost - backend posouvá začátek na první investici -->
                      {{ rangeStats?.range ? (rangeStats.range.from ? formatDate(rangeStats.range.from, 'd.m.Y') : '...') + ' - ' + (rangeStats.range.to ? formatDate(rangeStats.range.to, 'd.m.Y') : '...') : dateRange.from || dateRange.to ? (dateRange.from ? formatDate(dateRange.from, 'd.m.Y') : '...') + ' - ' + (dateRange.to ? formatDate(dateRange.to, 'd.m.Y') : '...') : formatDate(getCurrentYearYtdRange().from, 'd.m.Y') + ' - ' + formatDate(getCurrentYearYtdRange().to, 'd.m.Y') }}
                      <i class="mdi mdi-pencil ms-1"></i>
                    </span>
                    <div class="card__value" :class="{'ci ci--up': rangeStats.performance.performancePerc > 0, 'ci ci--down': rangeStats.performance.performancePerc < 0}">
                      <span>{{ rangeStats.performance.gain > 0 ? '+' : '' }}</span>{{ formatAmount(rangeStats.performance.gain, rangeStats.performance.currency) }}
                    </div>
                    <div class="card__subvalue" :class="{'ci ci--up': rangeStats.performance.performancePerc > 0, 'ci ci--down': rangeStats.performance.performancePerc < 0}">
                      <span>{{ rangeStats.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(rangeStats.performance.performancePerc) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-5" v-if="pendingDeposits.length > 0">
                <div class="col-12">
                  <div v-for="deposit of pendingDeposits" :key="deposit.id" class="alert alert-info">
                                      <div class="alert-icon">
                    <i class="mdi mdi-cash-multiple"></i>
                  </div>
                    <div class="alert-content" v-html="t('pendingDeposits.info_box_text_contract_strategy', {amount_formatted: formatAmount(deposit.amount, deposit.currency)})">
                    </div>
                  </div>
                </div>
              </div>

            <div class="row mt-4">
              <div class="col-12">
                <div class="card card--flat">
                  <div class="card-body card-body--only-chart">
                    <div v-if="chartData" class="card-body__chart">
                      <LineChart
                          :series="[
                          {
                            name: t('contractOverview.value_portfolio'),
                            data: chartData,
                          }
                        ]"
                          :valuesType="'amount'"
                          :currency="contractStrategy.currency"
                          :categories="chartCategories"
                      ></LineChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-12 mt-4">
          <div class="row">
            <div class="col-12">
              <div v-if="loading.contract_strategy" class="text-center">
                <Skeletor width="100%" height="240" />
              </div>
              <div v-if="!loading.contract_strategy" class="card card--no-header h-100">
                <div class="card-body">
                  <div class="info-table">
                    <div class="info-table__row row">
                      <div class="info-table__label col-12">
                        {{ t('contractOverview.contract_type') }}
                      </div>
                      <div class="info-table__value col-12">
                        {{ contractStrategy.contract.contract_type?.name }}
                      </div>
                    </div>
                    <div class="info-table__row row" v-if="contractStrategy?.label_text">
                      <div class="info-table__label col-12">
                        Můj název
                      </div>
                      <div class="info-table__value col-12">
                        {{ contractStrategy?.label_text }}
                      </div>
                    </div>
                    <div class="info-table__row row">
                      <div class="info-table__label col-12">
                        {{ t('contractOverview.contract_number') }}
                      </div>
                      <div class="info-table__value col-12">
                        {{ contractStrategy.variable_symbol }}
                      </div>
                    </div>
                    <div class="info-table__row row">
                      <div class="info-table__label col-12">
                        {{ t('contractOverview.strategy') }}
                      </div>
                      <div class="info-table__value col-12">
                        {{ contractStrategy?.strategy?.strategy_name }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-2 mt-lg-3">
            <!--            <div class="col-6">
                          <router-link class="btn btn-primary w-100" to="/service-requirements">Vložit prostředky</router-link>
                        </div>-->
            <div class="col-12">
              <router-link class="btn btn-primary w-100" to="/service-requirements/create">{{ t('contractOverview.service_request') }}</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="page-section page-section--tabs mt-4">
    <div class="page-section__content">

      <ul class="nav--tabs nav nav--pills" id="pills-tab" role="tablist">
        <li class="nav-item" :class="showInstructions ? 'col-3' : 'col-4'" role="presentation">
          <button class="nav-link active" id="contract-tab-composition" data-bs-toggle="pill" data-bs-target="#contract-tab-composition-content" type="button" role="tab" aria-controls="contract-tab-composition-content" aria-selected="true">{{ t('contractOverview.composition') }}</button>
        </li>
        <li class="nav-item" :class="showInstructions ? 'col-3' : 'col-4'" role="presentation">
          <button class="nav-link" id="contract-tab-trades" data-bs-toggle="pill" data-bs-target="#contract-tab-trades-content" type="button" role="tab" aria-controls="contract-tab-trades-content" aria-selected="true">{{ t('contractOverview.trades') }}</button>
        </li>
        <li class="nav-item" :class="showInstructions ? 'col-3' : 'col-4'" role="presentation">
          <button class="nav-link" id="contract-tab-transactions" data-bs-toggle="pill" data-bs-target="#contract-tab-transactions-content" type="button" role="tab" aria-controls="contract-tab-transactions-content" aria-selected="false">{{ t('contractOverview.transactions') }}</button>
        </li>
        <li class="nav-item col-3" v-if="showInstructions" role="presentation">
          <button class="nav-link" id="contract-tab-instructions" data-bs-toggle="pill" data-bs-target="#contract-tab-instructions-content" type="button" role="tab" aria-controls="contract-tab-instructions-content" aria-selected="false">{{ t('contractOverview.instructions') }}</button>
        </li>
      </ul>
      <div class="tab-content" id="pills-tabContent">
        <div class="tab-pane fade show active" id="contract-tab-composition-content" role="tabpanel" aria-labelledby="contract-tab-composition-content-tab">
          <div class="card">
            <div class="card-body card-body--only-chart" v-if="composition">
              <div class="card-body__chart card-body__chart--hauto">
                <PieChart
                    :view="'withTable'"
                    :loading="loading.contract_strategy_composition"
                    :series="[
                          {
                            name: t('contractOverview.composition_of_portfolio'),
                            data: composition
                          }
                        ]"
                ></PieChart>
              </div>
            </div>
          </div>
        </div>
        <div class="tab-pane fade" id="contract-tab-trades-content" role="tabpanel" aria-labelledby="contract-tab-trades-content-tab">
          <div v-if="loading.contract_strategy_trades" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('contractOverview.loading') }}</span>
            </div>
          </div>
          <div v-if="!loading.contract_strategy_trades">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                      <tr>
                        <th scope="col">
                          {{ t('contractOverview.date') }}
                          <div class="table__sub-value">{{ t('contractOverview.reference_number') }}</div>
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.type') }}
                          <div class="table__sub-value">{{ t('contractOverview.isin') }}</div>
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.strategy') }}
                          <div class="table__sub-value">{{ t('contractOverview.amount') }} | {{ t('contractOverview.quantity') }} | {{ t('contractOverview.price') }}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="trade of trades" :key="trade">
                        <td>
                          {{ formatDate(trade.transaction_date, 'd.m.Y H:i') }}
                          <div class="table__sub-value">{{ trade.trans_ref }}</div>
                        </td>
                        <td>
                          {{ trade.type?.sec_transaction_type }} - {{ trade.sec_account?.equity_name }}
                          <div v-if="trade.sec_account" class="table__sub-value">{{ trade.sec_account?.isin }}</div>
                        </td>
                        <td>
                          {{ trade.contract_strategy?.strategy?.name }}
                          <div class="table__sub-value">{{ formatAmount(trade.amount, trade.currency, 2, true) }} | {{ formatNumber(trade.quantity, 5, true) }} ks | {{ formatAmount(trade.amount/trade.quantity, trade.currency, 2, true) }} / ks</div>
                        </td>
                      </tr>
                      <tr v-if="!trades.length">
                        <td colspan="3" class="table__empty-td">
                          <div class="table__empty-text">
                            {{ t('contractOverview.no_trades_yet') }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="tab-pane fade" id="contract-tab-transactions-content" role="tabpanel" aria-labelledby="contract-tab-transactions">
          <div v-if="loading.contract_strategy_transactions" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('contractOverview.loading') }}</span>
            </div>
          </div>
          <div v-if="!loading.contract_strategy_transactions">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                      <tr>
                        <th scope="col">
                          {{ t('contractOverview.date') }}
                          <div class="table__sub-value">{{ t('contractOverview.reference_number') }}</div>
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.type') }}
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.strategy') }}
                          <div class="table__sub-value">{{ t('contractOverview.amount') }}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="transaction of transactions" :key="transaction">
                        <td>
                          {{ formatDate(transaction.transaction_date, 'd.m.Y H:i') }}
                          <div class="table__sub-value">{{ transaction.trans_ref }}</div>
                        </td>
                        <td>
                          {{ transaction.type?.cash_transaction_type }}
                          <div class="table__sub-value">{{ transaction.fee_type?.fee_type_name }}</div>
                        </td>
                        <td>
                          {{ transaction.contract_strategy?.strategy?.name }}
                          <div class="table__sub-value">{{ formatAmount(transaction.amount, transaction.currency) }}</div>
                        </td>
                      </tr>
                      <tr v-if="transactions && !transactions.length">
                        <td colspan="3" class="table__empty-td">
                          <div class="table__empty-text">
                            {{ t('contractOverview.no_transactions_yet') }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="tab-pane fade" id="contract-tab-instructions-content" role="tabpanel" aria-labelledby="contract-tab-instructions-content-tab">
          <div v-if="loading.contract_strategy_instructions" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">  {{ t('contractOverview.loading') }}</span>
            </div>
          </div>
          <div v-if="!loading.contract_strategy_instructions">
            <div class="card shadow">
              <div class="card-body">
                <InstructionsTableComponent :instructions="instructions"></InstructionsTableComponent>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
